import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, ShieldAlert, Search, Trash2 } from 'lucide-react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import api from '../../services/api';
import { useToast } from '../../hooks/use-toast';

type Tipo = 'TICKETS' | 'CONTATOS' | 'TUDO';

interface Job {
  tipo: Tipo;
  status: 'executando' | 'concluido' | 'erro';
  progresso: string | null;
  erro: string | null;
  resultado: Record<string, number> | null;
}

interface RegistroAuditoria {
  id: string;
  categoria: 'DADOS' | 'EMPRESA';
  empresaNome: string;
  tipo: string;
  periodoDe?: string | null;
  periodoAte?: string | null;
  usuarioNome?: string | null;
  usuarioEmail?: string | null;
  status: string;
  erro?: string | null;
  resumo?: Record<string, number> | null;
  dataInicio: string;
  dataFim?: string | null;
}

const PALAVRA = 'CONFIRMAR';

const OPCOES: { valor: Tipo; titulo: string; descricao: string }[] = [
  { valor: 'TICKETS', titulo: 'Tickets e mensagens', descricao: 'Apaga as conversas do período com mensagens e arquivos de mídia. Os contatos permanecem.' },
  { valor: 'CONTATOS', titulo: 'Contatos', descricao: 'Apaga contatos sem conversa com atendente nem agendamento. Quem conversou com atendente é sempre preservado.' },
  { valor: 'TUDO', titulo: 'Tudo', descricao: 'Apaga tickets, mensagens, mídias e, em seguida, os contatos que ficarem sem conversa.' },
];

const NOME_TIPO: Record<string, string> = { TICKETS: 'Tickets e mensagens', CONTATOS: 'Contatos', TUDO: 'Tudo', EMPRESA: 'Empresa' };

const extractError = (e: any) => e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Erro inesperado';
const n = (v?: number | null) => (v ?? 0).toLocaleString('pt-BR');
const dataHora = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—');
const dataCurta = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : 'início');

const textoResumo = (r?: Record<string, number> | null) => {
  if (!r) return '—';
  const partes: string[] = [];
  if (r.conversasRemovidas !== undefined) partes.push(`${n(r.conversasRemovidas)} conversa(s)`);
  if (r.mensagensRemovidas !== undefined) partes.push(`${n(r.mensagensRemovidas)} mensagem(ns)`);
  if (r.arquivosRemovidos !== undefined) partes.push(`${n(r.arquivosRemovidos)} arquivo(s)`);
  if (r.contatosRemovidos !== undefined) partes.push(`${n(r.contatosRemovidos)} contato(s)`);
  if (r.contatosPreservados) partes.push(`${n(r.contatosPreservados)} preservado(s)`);
  return partes.length ? partes.join(' · ') : '—';
};

const ExclusaoDadosConfig: React.FC = () => {
  const { toast } = useToast();
  const [empresas, setEmpresas] = useState<{ id: string; nome: string }[]>([]);
  const [empresaId, setEmpresaId] = useState('');
  const [tipo, setTipo] = useState<Tipo>('TICKETS');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [apenasFinalizados, setApenasFinalizados] = useState(true);
  const [escopoContatos, setEscopoContatos] = useState<'GRUPOS' | 'TODOS'>('GRUPOS');
  const [simulacao, setSimulacao] = useState<Record<string, number> | null>(null);
  const [simulando, setSimulando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [palavra, setPalavra] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [job, setJob] = useState<Job | null>(null);
  const [auditoria, setAuditoria] = useState<RegistroAuditoria[]>([]);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const executando = job?.status === 'executando';
  const pedidoValido = !!empresaId && !!ate;

  const corpo = () => ({
    tipo,
    de: de ? `${de}T00:00:00` : null,
    // Inclui o dia final inteiro
    ate: ate ? `${ate}T23:59:59.999` : null,
    apenasFinalizados,
    escopoContatos,
  });

  const carregarAuditoria = useCallback(async () => {
    try {
      const { data } = await api.get('/superadmin/auditoria-exclusoes', { params: { limite: 100 } });
      setAuditoria(data.registros || []);
    } catch (e) {
      toast({ title: 'Erro ao carregar a auditoria', description: extractError(e), variant: 'destructive' });
    }
  }, [toast]);

  useEffect(() => {
    api.get('/superadmin/retencao-midias').then(({ data }) => {
      setEmpresas((data.empresas || []).map((i: any) => ({ id: i.empresa.id, nome: i.empresa.nome })));
    }).catch((e) => toast({ title: 'Erro ao carregar empresas', description: extractError(e), variant: 'destructive' }));
    carregarAuditoria();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [carregarAuditoria, toast]);

  // Retoma o acompanhamento se já houver exclusão em andamento nesta empresa
  useEffect(() => {
    setJob(null);
    if (!empresaId) return;
    api.get(`/superadmin/exclusao-dados/${empresaId}/status`).then(({ data }) => {
      setJob(data.job);
      if (data.job?.status === 'executando') acompanhar(empresaId);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  // Qualquer mudança no pedido invalida a simulação anterior
  useEffect(() => { setSimulacao(null); }, [empresaId, tipo, de, ate, apenasFinalizados, escopoContatos]);

  const acompanhar = (id: string) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/superadmin/exclusao-dados/${id}/status`);
        setJob(data.job);
        if (data.job && data.job.status !== 'executando') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          carregarAuditoria();
        }
      } catch { /* tenta de novo no próximo ciclo */ }
    }, 2000);
  };

  const simular = async () => {
    setSimulando(true);
    try {
      const { data } = await api.post(`/superadmin/exclusao-dados/${empresaId}/simular`, corpo());
      setSimulacao(data.simulacao);
    } catch (e) {
      toast({ title: 'Erro na simulação', description: extractError(e), variant: 'destructive' });
    } finally {
      setSimulando(false);
    }
  };

  const executar = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post(`/superadmin/exclusao-dados/${empresaId}/executar`, { ...corpo(), confirmacao: palavra });
      setJob(data.job);
      setConfirmando(false);
      setPalavra('');
      acompanhar(empresaId);
    } catch (e) {
      toast({ title: 'Não foi possível excluir', description: extractError(e), variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const nomeEmpresa = empresas.find((e) => e.id === empresaId)?.nome || '';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-red-500" /> Exclusão de tickets, mensagens e contatos</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Execução sempre manual, por empresa e período. Nunca é agendada. A exclusão é definitiva e fica registrada na auditoria abaixo, que não pode ser apagada.
        </p>
      </div>

      <Card className="p-4 space-y-4">
        <div className="space-y-1">
          <Label htmlFor="exc-empresa">Empresa</Label>
          <select
            id="exc-empresa"
            className="w-full h-10 rounded-md border bg-background px-3 text-sm"
            value={empresaId}
            onChange={(e) => setEmpresaId(e.target.value)}
            disabled={executando}
          >
            <option value="">Selecione…</option>
            {empresas.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
          </select>
        </div>

        <div className="grid gap-2 md:grid-cols-3">
          {OPCOES.map((o) => (
            <button
              key={o.valor}
              type="button"
              disabled={executando}
              onClick={() => setTipo(o.valor)}
              className={`text-left rounded-md border p-3 transition ${tipo === o.valor ? 'border-red-500 bg-red-50 dark:bg-red-950/30' : 'hover:bg-muted'}`}
            >
              <p className="font-medium text-sm">{o.titulo}</p>
              <p className="text-xs text-muted-foreground mt-1">{o.descricao}</p>
            </button>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="exc-de">De (opcional)</Label>
            <Input id="exc-de" type="date" value={de} onChange={(e) => setDe(e.target.value)} disabled={executando} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="exc-ate">Até (obrigatório)</Label>
            <Input id="exc-ate" type="date" value={ate} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setAte(e.target.value)} disabled={executando} />
          </div>
          {tipo !== 'CONTATOS' && (
            <div className="flex items-end gap-2 pb-2">
              <Switch id="exc-fin" checked={apenasFinalizados} onCheckedChange={setApenasFinalizados} disabled={executando} />
              <Label htmlFor="exc-fin">Apenas tickets finalizados</Label>
            </div>
          )}
        </div>
        {tipo !== 'TICKETS' && (
          <div className="space-y-1">
            <Label htmlFor="exc-escopo">Quais contatos</Label>
            <select
              id="exc-escopo"
              className="w-full h-10 rounded-md border bg-background px-3 text-sm"
              value={escopoContatos}
              onChange={(e) => setEscopoContatos(e.target.value as 'GRUPOS' | 'TODOS')}
              disabled={executando}
            >
              <option value="GRUPOS">Somente contatos de grupos (criados por mensagem em grupo)</option>
              <option value="TODOS">Todos os contatos do período (inclui manuais e importados)</option>
            </select>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          O período considera a última atividade do ticket ou a última interação do contato (na falta dela, a data de criação).
        </p>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={simular} disabled={!pedidoValido || simulando || executando}>
            {simulando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />} Simular
          </Button>
          <Button variant="destructive" onClick={() => setConfirmando(true)} disabled={!pedidoValido || executando}>
            <Trash2 className="h-4 w-4 mr-2" /> Excluir
          </Button>
        </div>

        {simulacao && (
          <div className="rounded-md border p-3 text-sm space-y-1">
            <p className="font-medium">Seriam excluídos:</p>
            {simulacao.conversas !== undefined && <p>{n(simulacao.conversas)} conversa(s) com {n(simulacao.mensagens)} mensagem(ns)</p>}
            {simulacao.contatos !== undefined && <p>{n(simulacao.contatos)} contato(s) ({n(simulacao.contatosPreservados)} preservado(s) por ter conversa ou agendamento)</p>}
          </div>
        )}

        {job && (
          <div className="rounded-md border p-3 text-sm">
            {job.status === 'executando' && <p className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {job.progresso || 'Excluindo…'}</p>}
            {job.status === 'concluido' && <p className="text-green-700 dark:text-green-400">Exclusão concluída: {textoResumo(job.resultado)}</p>}
            {job.status === 'erro' && <p className="text-red-600 dark:text-red-400">Exclusão falhou: {job.erro}</p>}
          </div>
        )}
      </Card>

      <div className="space-y-2">
        <h3 className="font-semibold">Auditoria de exclusões (dados e empresas)</h3>
        <p className="text-xs text-muted-foreground">Registro permanente por LGPD, separado dos demais logs. Somente leitura.</p>
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>Exclusão</TableHead>
                <TableHead>Período</TableHead>
                <TableHead>Responsável</TableHead>
                <TableHead>Resultado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auditoria.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Nenhuma exclusão registrada.</TableCell></TableRow>
              )}
              {auditoria.map((r) => (
                <TableRow key={`${r.categoria}-${r.id}`}>
                  <TableCell className="whitespace-nowrap">{dataHora(r.dataInicio)}</TableCell>
                  <TableCell>{r.empresaNome}</TableCell>
                  <TableCell>{NOME_TIPO[r.tipo] || r.tipo}</TableCell>
                  <TableCell className="whitespace-nowrap">{r.categoria === 'DADOS' ? `${dataCurta(r.periodoDe)} a ${dataCurta(r.periodoAte)}` : '—'}</TableCell>
                  <TableCell>{r.usuarioNome || r.usuarioEmail || '—'}</TableCell>
                  <TableCell>
                    <Badge variant={r.status === 'SUCESSO' ? 'default' : r.status === 'ERRO' ? 'destructive' : 'secondary'}>{r.status}</Badge>
                    {r.categoria === 'DADOS' && <p className="text-xs text-muted-foreground mt-1">{r.erro || textoResumo(r.resumo)}</p>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <AlertDialog open={confirmando} onOpenChange={(aberto) => { if (!enviando) { setConfirmando(aberto); if (!aberto) setPalavra(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir dados de {nomeEmpresa}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 p-2 text-red-700 dark:text-red-300 font-medium">
                  Exclusão definitiva: não pode ser revertida.
                </p>
                <p>
                  {OPCOES.find((o) => o.valor === tipo)?.titulo}, período de {dataCurta(de ? de : null)} até {ate ? new Date(`${ate}T00:00:00`).toLocaleDateString('pt-BR') : '—'}
                  {tipo !== 'CONTATOS' && apenasFinalizados ? ', somente tickets finalizados' : ''}{tipo !== 'TICKETS' ? (escopoContatos === 'GRUPOS' ? '; contatos: somente de grupos' : '; contatos: TODOS') : ''}.
                </p>
                <p>Digite <strong>{PALAVRA}</strong> para confirmar. A ação ficará registrada na auditoria com seu nome.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={palavra} onChange={(e) => setPalavra(e.target.value)} placeholder={PALAVRA} autoFocus />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={enviando}>Cancelar</AlertDialogCancel>
            <Button variant="destructive" onClick={executar} disabled={palavra.trim() !== PALAVRA || enviando}>
              {enviando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Excluir definitivamente
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExclusaoDadosConfig;
